/**
 * Session initialization module
 * This module initializes the session management system and ensures
 * that a session ID is available from the start of the application.
 */

import {
  getSessionId,
  saveSessionId,
  debugSession,
  clearSessionId,
} from "./sessionManager";

/**
 * Initialize the session management system
 * This function should be called as early as possible in the application lifecycle
 */
export function initializeSession(): void {
  if (typeof window !== "undefined") {
    console.log("🚀 Initializing session management system");

    // Check if we already have a session ID
    const existingSessionId = getSessionId();
    if (existingSessionId) {
      console.log("🔑 Using existing session ID:", existingSessionId);
    } else {
      // No longer creating a temporary ID
      // The backend will generate the session ID on first request
      console.log(
        "⏳ No session ID found, waiting for backend to generate one"
      );
    }

    // Debug the session state
    debugSession();

    // Add event listener for storage changes to keep session ID in sync across tabs
    window.addEventListener("storage", (event) => {
      if (event.key === "app_session_id" && event.newValue) {
        console.log("📥 Session ID updated in another tab:", event.newValue);
        // No need to call saveSessionId here as it would trigger another storage event
      }
    });

    // Log session ID on every page load/navigation for debugging
    console.log("📊 Current session state on page load:");
    debugSession();
  }
}

// Auto-initialize when this module is imported
initializeSession();

// Export a function to manually re-initialize if needed
export function reinitializeSession(): void {
  console.log("🔄 Manually re-initializing session");
  initializeSession();
}

// Export a function to force a new session ID
export function forceNewSession(): void {
  console.log("🆕 Requesting new session");
  // Clear the current session ID
  // The next API request will trigger the backend to generate a new one
  clearSessionId();
  console.log(
    "🔑 Cleared session ID, backend will generate a new one on next request"
  );
  debugSession();
}
